import React, { useState, useRef, useEffect } from "react";
import { Input, Tooltip } from "antd";
import {
  RobotOutlined,
  CloseOutlined,
  SendOutlined,
  ReloadOutlined,
  PaperClipOutlined,
  CloseCircleOutlined,
  LockOutlined,
  FileOutlined,
  HistoryOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../utils/interceptors/axiosInstance";
import { getApiBaseUrl } from "../../shared/config/tenantConfiguration";
import { useLocation } from "react-router-dom";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { useTenant } from "../../store/context/tenant-context";
import { hexToRgba } from "../../utils/helper/colorUtils";
import { useNotification } from "../../store/context/notification-context";
import { postAuthenticatedRequest } from "../../utils/api/requests/request";
import {
  getAgentTransactionDetails,
  isTerminalAgentTransactionStatus,
  normalizeTransactionStatusEvent,
  transactionEventMatches,
  transactionRecordStatus,
} from "../../shared/utils/transactionNotifications";

const newConvId = () => Math.random().toString(36).slice(2, 10);
const transactionSecondsLeft = (expiresAt, fallback = 60) => {
  if (!expiresAt) return fallback;
  const remaining = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000);
  return Number.isFinite(remaining) ? Math.max(0, remaining) : fallback;
};

// Renders simple markdown: **bold**, *italic*, newlines → <br />
const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, li) => {
    // Split by **bold** and *italic* patterns
    const parts = [];
    const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let last = 0;
    let m;
    while ((m = pattern.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      if (m[0].startsWith("**")) {
        parts.push(<strong key={m.index}>{m[2]}</strong>);
      } else {
        parts.push(<em key={m.index}>{m[3]}</em>);
      }
      last = m.index + m[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    return (
      <span key={li}>
        {parts}
        {li < lines.length - 1 && <br />}
      </span>
    );
  });
};

const SUGGESTED_PROMPTS = [
  "Show my account balance",
  "Show my recent transactions",
  "Show my loan applications",
  "What can you help me with?",
];

// ─── Transaction Confirmation Card ──────────────────────────────────────────
const TransactionConfirmCard = ({ comp, authHint, primary, transactionState, onTimeout }) => {
  const TIMEOUT_SECS = 60;
  const data = comp?.data ?? comp?.Data ?? {};
  const expiresAt = transactionState?.expiresAt ?? data?.expiresAt ?? data?.ExpiresAt;
  const [secondsLeft, setSecondsLeft] = useState(() => transactionSecondsLeft(expiresAt, TIMEOUT_SECS));
  const status = transactionState?.status || "AWAITING_AUTHORISATION";
  const isTerminal = isTerminalAgentTransactionStatus(status);

  useEffect(() => {
    setSecondsLeft(transactionSecondsLeft(expiresAt, TIMEOUT_SECS));
  }, [expiresAt]);

  useEffect(() => {
    if (isTerminal) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          onTimeout?.();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isTerminal, onTimeout]);

  const description = authHint?.description || comp?.title || "Confirm transaction";
  const method = authHint?.method || "PIN";
  const isUrgent = secondsLeft <= 15 && !isTerminal;
  const statusMessage = transactionState?.message;

  if (status === "SUCCESSFUL") {
    const reference = transactionState?.record?.requestId ?? data?.requestId ?? data?.RequestId;
    return (
      <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CheckCircleOutlined style={{ color: "#16a34a", fontSize: 22 }} />
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#166534" }}>Transaction successful</p>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#166534", lineHeight: 1.5 }}>
              {statusMessage || description}
            </p>
          </div>
        </div>
        {reference && <p style={{ margin: "10px 0 0 32px", fontSize: 10, color: "#15803d" }}>Reference: {reference}</p>}
      </div>
    );
  }

  if (status === "FAILED" || status === "CANCELLED") {
    return (
      <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <CloseCircleOutlined style={{ color: "#dc2626", fontSize: 20, flexShrink: 0 }} />
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#dc2626" }}>
            {status === "CANCELLED" ? "Transaction cancelled" : "Transaction failed"}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: "#991b1b", lineHeight: 1.5 }}>
            {statusMessage || "The transaction could not be completed. You can try again."}
          </p>
        </div>
      </div>
    );
  }

  if (status === "TIMED_OUT") {
    return (
      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <WarningOutlined style={{ color: "#d97706", fontSize: 20, flexShrink: 0 }} />
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#92400e" }}>Transaction update is taking longer</p>
          <p style={{ margin: 0, fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
            Check your transaction history before trying the transaction again.
          </p>
        </div>
      </div>
    );
  }

  if (status === "PROCESSING") {
    return (
      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <LoadingOutlined style={{ color: "#2563eb", fontSize: 20, flexShrink: 0 }} />
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: "#1d4ed8" }}>Transaction processing</p>
          <p style={{ margin: 0, fontSize: 11, color: "#1e40af", lineHeight: 1.5 }}>
            {statusMessage || "Authorization was received. Waiting for the final transaction update."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", border: `1px solid ${isUrgent ? "#fca5a5" : hexToRgba(primary, 0.3)}`, borderRadius: 12, padding: "14px 16px", boxShadow: `0 2px 12px ${hexToRgba(primary, 0.1)}`, transition: "border-color 0.3s" }}>
      <p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: 13, color: "#0f0f0f" }}>
        {comp?.title || "Confirm Transaction"}
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "#555", lineHeight: 1.5 }}>{description}</p>

      {/* Awaiting auth notice */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, background: hexToRgba(primary, 0.05), border: `1px solid ${hexToRgba(primary, 0.15)}`, borderRadius: 10, padding: "11px 13px" }}>
        <LockOutlined style={{ fontSize: 20, color: primary, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 12, color: "#1a1a1a" }}>
            {method} authorization required
          </p>
          <p style={{ margin: 0, fontSize: 11, color: "#555", lineHeight: 1.5 }}>
            A notification has been sent to your registered device. Enter your {method} there to complete this transaction.
          </p>
        </div>

        {/* Countdown ring */}
        <div style={{
          flexShrink: 0,
          width: 46, height: 46, borderRadius: "50%",
          border: `3px solid ${isUrgent ? "#f87171" : hexToRgba(primary, 0.45)}`,
          background: isUrgent ? "#fff5f5" : hexToRgba(primary, 0.04),
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "border-color 0.3s, background 0.3s",
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: isUrgent ? "#dc2626" : primary, fontVariantNumeric: "tabular-nums" }}>
            {secondsLeft}s
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── File Upload Prompt Card ─────────────────────────────────────────────────
const FileUploadPromptCard = ({ comp, fileUploadHint, onTrigger, primary }) => {
  const hint = fileUploadHint || {};
  const fmtSize = (bytes) => bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
  return (
    <div style={{ background: hexToRgba(primary, 0.04), border: `1.5px dashed ${hexToRgba(primary, 0.35)}`, borderRadius: 12, padding: "14px 16px" }}>
      <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 13, color: "#0f0f0f" }}>
        <FileOutlined style={{ marginRight: 6, color: primary }} />
        {hint.label || comp?.title || "Upload Document"}
      </p>
      {hint.instructions && (
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "#555", lineHeight: 1.5 }}>{hint.instructions}</p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {hint.acceptedMimeTypes?.map((mt) => (
          <span key={mt} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: hexToRgba(primary, 0.1), color: primary, border: `1px solid ${hexToRgba(primary, 0.2)}` }}>
            {mt.split("/")[1]?.toUpperCase() || mt}
          </span>
        ))}
        {hint.maxSizeBytes && (
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, background: "#f5f5f5", color: "#666", border: "1px solid #e0e0e0" }}>
            Max {fmtSize(hint.maxSizeBytes)}
          </span>
        )}
      </div>
      <button
        onClick={onTrigger}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1.5px solid ${primary}`, background: hexToRgba(primary, 0.06), color: primary, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
      >
        <PaperClipOutlined />
        Choose File
      </button>
    </div>
  );
};

const AgentCopilotPanel = () => {
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [convId, setConvId]       = useState(newConvId);
  const [stagedFiles, setStagedFiles] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convTitle, setConvTitle] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const bottomRef    = useRef(null);
  const inputRef     = useRef(null);
  const fileInputRef = useRef(null);
  const hasLoadedLatest = useRef(false);
  const messagesRef = useRef(messages);
  const { signalRNotification } = useNotification();
  const apiBase = getApiBaseUrl();
  const tenant = useTenant();
  const primary = tenant?.theme?.colorPrimary || "#0b3d2e";
  const panelGradient = primary;

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && !hasLoadedLatest.current) {
      hasLoadedLatest.current = true;
      loadLatestConversation();
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const loadLatestConversation = async () => {
    try {
      const res  = await axiosInstance.get("/api/agent/conversations/latest");
      const data = res?.data?.outData ?? res?.data ?? {};
      if (data.conversationId && data.messages?.length) {
        setConvId(data.conversationId);
        setConvTitle(data.title || "");
        setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
      }
    } catch { /* start fresh if no previous conversation */ }
  };

  const loadConversations = async () => {
    setHistoryLoading(true);
    try {
      const res  = await axiosInstance.get("/api/agent/conversations");
      const data = res?.data?.outData ?? res?.data ?? {};
      setConversations(data.conversations ?? []);
    } catch {}
    finally { setHistoryLoading(false); }
  };

  const loadConversation = async (id) => {
    try {
      const res  = await axiosInstance.get(`/api/agent/conversations/${id}`);
      const data = res?.data?.outData ?? res?.data ?? {};
      if (data.conversationId && data.messages?.length) {
        setConvId(data.conversationId);
        setConvTitle(data.title || "");
        setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
        setShowHistory(false);
      }
    } catch {}
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64Data = ev.target.result.split(",")[1];
        setStagedFiles((prev) => [
          ...prev,
          { fileName: file.name, mimeType: file.type, base64Data, size: file.size },
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed && stagedFiles.length === 0) return;
    if (loading || chatLocked) return;
    const filesToSend = stagedFiles.map(({ fileName, mimeType, base64Data }) => ({ fileName, mimeType, base64Data }));
    const displayFiles = stagedFiles.length > 0 ? stagedFiles.map((f) => ({
      fileName: f.fileName,
      mimeType: f.mimeType,
      previewUrl: f.mimeType?.startsWith("image/") ? `data:${f.mimeType};base64,${f.base64Data}` : null,
    })) : undefined;
    const userMsg = { role: "user", content: trimmed || "(file attached)", files: displayFiles };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setStagedFiles([]);
    setLoading(true);
    try {
      const payload = {
        conversationId: convId,
        messages: history.map(({ role, content }) => ({ role, content })),
      };
      if (filesToSend.length > 0) payload.files = filesToSend;
      const res = await axiosInstance.post("/api/agent/chat", payload);
      // The response may be nested in outData depending on server wrapper
      const data = res?.data?.outData ?? res?.data ?? {};
      const reply =
        data?.message ??
        data?.content ??
        "I received your message but have no response to show.";
      const assistantMessage = {
        role: "assistant",
        content: reply,
        components: data?.components,
        interactionHint: data?.interactionHint,
        authHint: data?.authHint,
        fileUploadHint: data?.fileUploadHint,
        suggestions: data?.suggestions ?? null,
        isError: data?.isError === true,
      };
      const transaction = getAgentTransactionDetails(assistantMessage);
      if (transaction && data?.interactionHint === "AWAIT_AUTH") {
        assistantMessage.transactionState = {
          status: "AWAITING_AUTHORISATION",
          transactionId: transaction.transactionId,
          requestId: transaction.requestId,
          expiresAt: transaction.expiresAt,
        };
      }
      setMessages([...history, assistantMessage]);
    } catch (err) {
      const errMsg =
        err?.response?.status === 401
          ? "Session expired. Please log in again."
          : err?.response?.data?.message ??
            "Sorry, I could not process your request. Please try again.";
      setMessages([
        ...history,
        { role: "assistant", content: errMsg, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const awaitFile = lastAssistant?.interactionHint === "AWAIT_FILE";
  const awaitFileHint = awaitFile ? lastAssistant?.fileUploadHint : null;
  const activeTransactionMessage = [...messages].reverse().find((message) => {
    const transaction = getAgentTransactionDetails(message);
    if (!transaction) return false;
    const status = message?.transactionState?.status ||
      (message?.interactionHint === "AWAIT_AUTH" ? "AWAITING_AUTHORISATION" : null);
    return status && !isTerminalAgentTransactionStatus(status);
  });
  const chatLocked = Boolean(activeTransactionMessage);

  useEffect(() => {
    const event = normalizeTransactionStatusEvent(signalRNotification);
    if (!event) return;

    const matchingMessage = [...messagesRef.current].reverse().find((message) => {
      const transaction = getAgentTransactionDetails(message);
      return transaction && transactionEventMatches(event, transaction);
    });
    if (!matchingMessage) return;

    const transaction = getAgentTransactionDetails(matchingMessage);
    const applyUpdate = (update) => {
      setMessages((current) => current.map((message) => {
        const details = getAgentTransactionDetails(message);
        if (!details || !transactionEventMatches(event, details)) return message;
        return {
          ...message,
          transactionState: {
            ...message.transactionState,
            transactionId: details.transactionId,
            requestId: details.requestId,
            expiresAt: details.expiresAt,
            ...update,
          },
        };
      }));
    };

    // Reuse the same optimistic status update normal transaction screens receive.
    applyUpdate({ status: event.status, message: event.message });

    // Then refresh the existing transaction record. This resolves ambiguous
    // legacy events such as `initiated`, including transactions that completed
    // synchronously before the SignalR message arrived.
    const transactionId = Number(transaction.transactionId);
    if (!Number.isFinite(transactionId)) return;

    let cancelled = false;
    const refresh = async () => {
      try {
        const response = await postAuthenticatedRequest(
          "GetSelfServiceTransactionsQuery",
          { operation: "GET", transactionId, pageNumber: 1, pageSize: 1 }
        );
        const record = response?.outData?.data?.transfers?.[0];
        if (!cancelled && record) {
          applyUpdate({
            status: transactionRecordStatus(record),
            message: record?.transactionStatusDescription || event.message,
            requestId: record?.requestId ?? transaction.requestId,
            record,
          });
        }
      } catch (error) {
        console.warn("Unable to refresh Agent transaction status", error);
      }
    };

    refresh();
    return () => { cancelled = true; };
  }, [signalRNotification]);

  const markTransactionTimedOut = (transaction) => {
    setMessages((current) => current.map((message) => {
      const details = getAgentTransactionDetails(message);
      if (!details || !transactionEventMatches(transaction, details)) return message;
      if (isTerminalAgentTransactionStatus(message?.transactionState?.status)) return message;
      return {
        ...message,
        transactionState: {
          ...message.transactionState,
          status: "TIMED_OUT",
          message: "The transaction update is taking longer than expected.",
        },
      };
    }));
  };

  const reset = () => {
    if (chatLocked) return;
    setMessages([]);
    setStagedFiles([]);
    setConvId(newConvId());
    setConvTitle("");
  };

  return (
    <>
      {/* ── Floating trigger ─────────────────────────────── */}
      {!open && (
        <Tooltip title="BankLingo Copilot" placement="left">
          <button
            onClick={() => setOpen(true)}
            aria-label="Open AI Copilot"
            style={{
              position: "fixed",
              bottom: 28,
              right: 28,
              zIndex: 1200,
              width: 60,
              height: 60,
              borderRadius: "50%",
              background: panelGradient,
              border: "3px solid rgba(255,255,255,0.85)",
              cursor: "pointer",
              boxShadow: `0 4px 24px ${hexToRgba(primary, 0.55)}, 0 0 0 4px ${hexToRgba(primary, 0.25)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow =
                `0 8px 32px ${hexToRgba(primary, 0.65)}, 0 0 0 6px ${hexToRgba(primary, 0.3)}`;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow =
                `0 4px 24px ${hexToRgba(primary, 0.55)}, 0 0 0 4px ${hexToRgba(primary, 0.25)}`;
            }}
          >
            <RobotOutlined style={{ fontSize: 28, color: "#fff" }} />
          </button>
        </Tooltip>
      )}

      {/* ── Chat panel ───────────────────────────────────── */}
      {open && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            right: 0,
            zIndex: 1200,
            width: 380,
            height: "100vh",
            background: "#fff",
            boxShadow: "-6px 0 40px rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            borderLeft: "1px solid #e8e8e8",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              background:
                panelGradient,
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RobotOutlined style={{ fontSize: 20, color: "#fff" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  margin: 0,
                }}
              >
                BankLingo Copilot
              </p>
              <p
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.5)",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {convTitle || `Connected · ${apiBase}`}
              </p>
            </div>
            <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
              <Tooltip title="Conversation history">
                <button
                  onClick={() => { setShowHistory(true); loadConversations(); }}
                  disabled={chatLocked}
                  style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, width: 30, height: 30, cursor: chatLocked ? "not-allowed" : "pointer", opacity: chatLocked ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <HistoryOutlined style={{ color: "#fff", fontSize: 13 }} />
                </button>
              </Tooltip>
              <Tooltip title="New conversation">
                <button
                  onClick={reset}
                  disabled={chatLocked}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    border: "none",
                    borderRadius: 8,
                    width: 30,
                    height: 30,
                    cursor: chatLocked ? "not-allowed" : "pointer",
                    opacity: chatLocked ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ReloadOutlined style={{ color: "#fff", fontSize: 13 }} />
                </button>
              </Tooltip>
              <button
                onClick={() => setOpen(false)}
                style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <CloseOutlined style={{ color: "#fff", fontSize: 13 }} />
              </button>
            </div>
          </div>

          {/* Full-image lightbox */}
          {previewImg && (
            <div
              onClick={() => setPreviewImg(null)}
              style={{ position: "absolute", inset: 0, zIndex: 30, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}
            >
              <img src={previewImg} alt="preview" style={{ maxWidth: "92%", maxHeight: "88%", borderRadius: 10, objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }} />
              <span style={{ position: "absolute", top: 12, right: 14, color: "#fff", fontSize: 18, fontWeight: 700, cursor: "pointer", lineHeight: 1 }}>✕</span>
            </div>
          )}

          {/* History panel — overlays the chat */}
          {showHistory && (
            <div style={{ position: "absolute", inset: 0, zIndex: 20, background: "#fff", display: "flex", flexDirection: "column" }}>
              <div style={{ background: panelGradient, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <p style={{ fontWeight: 700, fontSize: 14, color: "#fff", margin: 0 }}>Conversation History</p>
                <button onClick={() => setShowHistory(false)} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CloseOutlined style={{ color: "#fff", fontSize: 13 }} />
                </button>
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                {historyLoading ? (
                  <p style={{ textAlign: "center", color: "#bbb", fontSize: 13, marginTop: 32 }}>Loading…</p>
                ) : conversations.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#bbb", fontSize: 13, marginTop: 32 }}>No past conversations</p>
                ) : conversations.map((conv) => (
                  <div
                    key={conv.conversationId}
                    onClick={() => loadConversation(conv.conversationId)}
                    style={{ padding: "11px 14px", borderRadius: 10, border: "1px solid #f0f0f0", cursor: "pointer", transition: "background 0.12s" }}
                    onMouseOver={(e) => { e.currentTarget.style.background = hexToRgba(primary, 0.05); }}
                    onMouseOut={(e) => { e.currentTarget.style.background = "#fff"; }}
                  >
                    <p style={{ fontSize: 13, fontWeight: 600, color: "#111", margin: "0 0 3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {conv.title || conv.conversationId}
                    </p>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(conv.lastMessageAt).toLocaleString()}</span>
                      <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 20, background: hexToRgba(primary, 0.08), color: primary, fontWeight: 600 }}>{conv.messageCount} msgs</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: "12px 14px", borderTop: "1px solid #f0f0f0" }}>
                <button
                  onClick={() => { reset(); setShowHistory(false); }}
                  disabled={chatLocked}
                  style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: `1.5px dashed ${hexToRgba(primary, 0.4)}`, background: hexToRgba(primary, 0.05), color: primary, fontWeight: 700, fontSize: 13, cursor: chatLocked ? "not-allowed" : "pointer", opacity: chatLocked ? 0.5 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}
                >
                  <PlusOutlined /> New Conversation
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {messages.length === 0 && !loading && (
              <div style={{ textAlign: "center", paddingTop: 32 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 18,
                    background: hexToRgba(primary, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 14px",
                    border: `1px solid ${hexToRgba(primary, 0.25)}`,
                  }}
                >
                  <RobotOutlined
                    style={{ fontSize: 32, color: primary }}
                  />
                </div>
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#0f0f0f",
                    margin: "0 0 4px",
                  }}
                >
                  BankLingo Copilot
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "#888",
                    margin: "0 0 22px",
                    lineHeight: 1.5,
                  }}
                >
                  Ask me anything about your accounts,
                  <br />
                  transactions, or loans.
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    justifyContent: "center",
                  }}
                >
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => sendMessage(p)}
                      style={{
                        fontSize: 12,
                        padding: "6px 13px",
                        borderRadius: 20,
                        border: `1px solid ${hexToRgba(primary, 0.3)}`,
                        background: hexToRgba(primary, 0.06),
                        color: primary,
                        cursor: "pointer",
                        fontWeight: 500,
                        transition: "all 0.15s",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = hexToRgba(primary, 0.12);
                        e.currentTarget.style.borderColor = primary;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = hexToRgba(primary, 0.06);
                        e.currentTarget.style.borderColor = hexToRgba(primary, 0.3);
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: "flex",
                    flexDirection:
                      msg.role === "user" ? "row-reverse" : "row",
                    gap: 8,
                    alignItems: "flex-start",
                  }}
                >
                  {msg.role === "assistant" && (
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        flexShrink: 0,
                        background: panelGradient,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: 2,
                      }}
                    >
                      <RobotOutlined style={{ fontSize: 14, color: "#fff" }} />
                    </div>
                  )}
                  <div
                    style={{
                      maxWidth: "80%",
                      padding: "10px 13px",
                      borderRadius:
                        msg.role === "user"
                          ? "16px 4px 16px 16px"
                          : "4px 16px 16px 16px",
                      background:
                        msg.role === "user"
                          ? panelGradient
                          : (msg.error || msg.isError)
                          ? "#fff5f5"
                          : "#f4f6f4",
                      color:
                        msg.role === "user"
                          ? "#fff"
                          : (msg.error || msg.isError)
                          ? "#c62828"
                          : "#1a1a1a",
                      fontSize: 13,
                      lineHeight: 1.55,
                      border:
                        msg.role === "assistant"
                          ? `1px solid ${(msg.error || msg.isError) ? "#ffcccc" : "#e8ede8"}`
                          : "none",
                      wordBreak: "break-word",
                    }}
                  >
                    {msg.role === "assistant" ? renderMarkdown(msg.content) : msg.content}
                    {msg.role === "user" && msg.files?.length > 0 && (
                      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {msg.files.map((f, fi) => (
                          f.previewUrl ? (
                            <div key={fi} onClick={() => setPreviewImg(f.previewUrl)} style={{ position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid rgba(255,255,255,0.3)", cursor: "zoom-in" }}>
                              <img src={f.previewUrl} alt={f.fileName} style={{ width: 60, height: 60, objectFit: "cover", display: "block" }} />
                            </div>
                          ) : (
                            <span key={fi} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, padding: "2px 7px", borderRadius: 12, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}>
                              <FileOutlined style={{ fontSize: 9 }} />
                              {f.fileName}
                            </span>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {msg.role === "assistant" && msg.components?.length > 0 && (
                  <div style={{ paddingLeft: 36, display: "flex", flexDirection: "column", gap: 8 }}>
                    {msg.components.map((comp, ci) => {
                      if ((comp.type ?? comp.Type) === "TRANSACTION_CONFIRMATION") {
                        return (
                          <TransactionConfirmCard
                            key={ci}
                            comp={comp}
                            authHint={msg.authHint}
                            primary={primary}
                            transactionState={msg.transactionState}
                            onTimeout={() => markTransactionTimedOut(getAgentTransactionDetails(msg))}
                          />
                        );
                      }
                      if (comp.type === "FILE_UPLOAD_PROMPT") {
                        return <FileUploadPromptCard key={ci} comp={comp} fileUploadHint={msg.fileUploadHint} onTrigger={() => fileInputRef.current?.click()} primary={primary} />;
                      }
                      return null;
                    })}
                  </div>
                )}
                {msg.role === "assistant" && msg.suggestions?.length > 0 && i === messages.length - 1 && (
                  <div style={{ paddingLeft: 36, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {msg.suggestions.map((s, si) => (
                      <button
                        key={si}
                        onClick={() => sendMessage(s.value || s.label)}
                        disabled={loading || chatLocked}
                        style={{
                          fontSize: 12, fontWeight: 500,
                          padding: "5px 13px", borderRadius: 20,
                          border: `1.5px solid ${hexToRgba(primary, 0.35)}`,
                          background: hexToRgba(primary, 0.06),
                          color: primary, cursor: (loading || chatLocked) ? "not-allowed" : "pointer",
                          opacity: (loading || chatLocked) ? 0.5 : 1, transition: "all 0.15s",
                          whiteSpace: "nowrap",
                        }}
                        onMouseOver={(e) => { if (!loading && !chatLocked) { e.currentTarget.style.background = hexToRgba(primary, 0.14); e.currentTarget.style.borderColor = primary; } }}
                        onMouseOut={(e) => { e.currentTarget.style.background = hexToRgba(primary, 0.06); e.currentTarget.style.borderColor = hexToRgba(primary, 0.35); }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    flexShrink: 0,
                    background:
                      panelGradient,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 2,
                  }}
                >
                  <RobotOutlined
                    style={{ fontSize: 14, color: "#fff" }}
                  />
                </div>
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#f4f6f4",
                    borderRadius: "4px 16px 16px 16px",
                    border: "1px solid #e8ede8",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: primary,
                        display: "inline-block",
                        animation: `blCopilotBounce 1.2s ease-in-out ${
                          i * 0.2
                        }s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div
            style={{
              padding: "12px 14px",
              borderTop: "1px solid #f0f0f0",
              background: "#fafafa",
              flexShrink: 0,
            }}
          >
            {/* Staged file chips */}
            {stagedFiles.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {stagedFiles.map((f, fi) => (
                  <span
                    key={fi}
                    style={{
                      display: "flex", alignItems: "center", gap: 4,
                      fontSize: 11, padding: f.mimeType?.startsWith("image/") ? "2px 8px 2px 2px" : "3px 8px 3px 10px",
                      borderRadius: 20,
                      background: hexToRgba(primary, 0.08),
                      border: `1px solid ${hexToRgba(primary, 0.25)}`,
                      color: primary,
                    }}
                  >
                    {f.mimeType?.startsWith("image/") ? (
                      <img src={`data:${f.mimeType};base64,${f.base64Data}`} alt={f.fileName}
                        onClick={() => setPreviewImg(`data:${f.mimeType};base64,${f.base64Data}`)}
                        style={{ width: 22, height: 22, borderRadius: 12, objectFit: "cover", flexShrink: 0, cursor: "zoom-in" }} />
                    ) : (
                      <FileOutlined style={{ fontSize: 10 }} />
                    )}
                    <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.fileName}</span>
                    <button
                      onClick={() => setStagedFiles((prev) => prev.filter((_, j) => j !== fi))}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, marginLeft: 2, color: primary, display: "flex", alignItems: "center" }}
                    >
                      <CloseCircleOutlined style={{ fontSize: 11 }} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-end",
              }}
            >
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || chatLocked}
                title="Attach file"
                style={{
                  background: awaitFile ? hexToRgba(primary, 0.1) : "transparent",
                  border: awaitFile ? `1.5px solid ${hexToRgba(primary, 0.35)}` : "1.5px solid transparent",
                  borderRadius: 10,
                  height: 36,
                  width: 36,
                  minWidth: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  padding: 0,
                  cursor: (loading || chatLocked) ? "not-allowed" : "pointer",
                  opacity: (loading || chatLocked) ? 0.5 : 1,
                  color: awaitFile ? primary : "#bbb",
                  transition: "all 0.2s",
                }}
              >
                <PaperClipOutlined style={{ fontSize: 16 }} />
              </button>
              <Input.TextArea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder={chatLocked ? "Waiting for the transaction update\u2026" : awaitFile ? "Describe the document and attach your file\u2026" : "Ask me anything\u2026 (Enter to send)"}
                autoSize={{ minRows: 1, maxRows: 4 }}
                style={{
                  flex: 1,
                  borderRadius: 12,
                  fontSize: 13,
                  resize: "none",
                  border: awaitFile ? `1.5px solid ${hexToRgba(primary, 0.4)}` : "1px solid #e0e0e0",
                }}
                disabled={loading || chatLocked}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={(!input.trim() && stagedFiles.length === 0) || loading || chatLocked}
                style={{
                  background: ((!input.trim() && stagedFiles.length === 0) || loading || chatLocked)
                    ? "#ccc"
                    : panelGradient,
                  border: "none",
                  borderRadius: 10,
                  height: 36,
                  width: 36,
                  minWidth: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  padding: 0,
                  cursor: ((!input.trim() && stagedFiles.length === 0) || loading || chatLocked) ? "not-allowed" : "pointer",
                  transition: "background 0.2s",
                }}
              >
                <SendOutlined style={{ fontSize: 15, color: "#fff" }} />
              </button>
            </div>
            <p
              style={{
                fontSize: 10,
                color: "#c0c0c0",
                margin: "6px 0 0",
                textAlign: "center",
              }}
            >
              Conv: {convId} · Msgs: {messages.length}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={awaitFileHint?.acceptedMimeTypes?.join(",") || "image/*,application/pdf"}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes blCopilotBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </>
  );
};

const AgentCopilotWidget = () => {
  useLocation(); // re-render on every navigation so the session check stays fresh
  if (!getSessionData()) return null;
  return <AgentCopilotPanel />;
};

export default AgentCopilotWidget;
